import type { TeamChatWirePayload } from '../types';
import { CUSTOMER_ACCESS_TOKEN_KEY } from '../../../services/auth/customerAuthApi';

type Handler = (payload: TeamChatWirePayload) => void;

const CHANNEL_NAME = 'coremonit-team-chat';

let singleton: TeamChatTransport | null = null;

export function getTeamChatTransport(tenantKey: string): TeamChatTransport {
  if (!singleton) singleton = new TeamChatTransport(tenantKey);
  else if (singleton.tenantKey !== tenantKey) {
    singleton.dispose();
    singleton = new TeamChatTransport(tenantKey);
  }
  return singleton;
}

/**
 * Lightweight real-time layer:
 * - BroadcastChannel for same-origin tabs (no extra infra).
 * - Optional native WebSocket when `import.meta.env.VITE_CHAT_WS_URL` is set (does not touch other app sockets).
 */
export class TeamChatTransport {
  readonly tenantKey: string;
  private handlers = new Set<Handler>();
  private bc: BroadcastChannel | null = null;
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private disposed = false;

  constructor(tenantKey: string) {
    this.tenantKey = tenantKey;
    const useWs = Boolean(import.meta.env.VITE_CHAT_WS_URL);
    if (!useWs && typeof BroadcastChannel !== 'undefined') {
      this.bc = new BroadcastChannel(CHANNEL_NAME);
      this.bc.onmessage = (ev: MessageEvent<TeamChatWirePayload>) => {
        const p = ev.data;
        if (!p || p.tenantKey !== this.tenantKey) return;
        this.dispatchLocal(p);
      };
    }
    this.tryConnectWs();
  }

  subscribe(h: Handler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  publish(payload: Omit<TeamChatWirePayload, 'tenantKey' | 'ts'> & { ts?: number }): void {
    const full: TeamChatWirePayload = {
      ...payload,
      tenantKey: this.tenantKey,
      ts: payload.ts ?? Date.now(),
    };
    try {
      if (this.bc) {
        this.bc.postMessage(full);
      } else {
        this.dispatchLocal(full);
      }
    } catch {
      this.dispatchLocal(full);
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(full));
      } catch {
        /* ignore */
      }
    }
  }

  private dispatchLocal(p: TeamChatWirePayload): void {
    for (const h of this.handlers) {
      try {
        h(p);
      } catch (e) {
        console.warn('[team-chat] handler error', e);
      }
    }
  }

  private tryConnectWs(): void {
    const url = import.meta.env.VITE_CHAT_WS_URL;
    if (!url || typeof WebSocket === 'undefined') return;
    if (this.disposed) return;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY) : null;
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    const fullUrl = `${url.replace(/\/$/, '')}${qs}`;

    try {
      const ws = new WebSocket(fullUrl);
      this.ws = ws;
      ws.onmessage = (ev) => {
        try {
          const p = JSON.parse(String(ev.data)) as TeamChatWirePayload;
          if (p?.tenantKey !== this.tenantKey) return;
          this.dispatchLocal(p);
        } catch {
          /* ignore */
        }
      };
      ws.onopen = () => {
        this.startPing();
        if (import.meta.env.DEV) console.info('[team-chat] websocket open', { url: fullUrl.split('?')[0] });
      };
      ws.onclose = () => {
        this.stopPing();
        this.scheduleReconnect();
      };
      ws.onerror = () => {
        /* onclose will reconnect */
      };
    } catch (e) {
      console.warn('[team-chat] websocket connect failed', e);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    if (!import.meta.env.VITE_CHAT_WS_URL) return;
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.ws) {
        try {
          this.ws.close();
        } catch {
          /* ignore */
        }
        this.ws = null;
      }
      this.tryConnectWs();
    }, 4000);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.publish({ type: 'ping' });
    }, 25_000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stopPing();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    try {
      this.bc?.close();
    } catch {
      /* ignore */
    }
    this.bc = null;
    this.handlers.clear();
  }
}

export function resetTeamChatTransportForTests(): void {
  singleton?.dispose();
  singleton = null;
}
